<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { getMedStratSigningUrl } from 'src/api/endpoints/loginWeb';
import FullViewLayout from 'src/components/layouts/FullViewLayout.vue';
import { handleApiRequest } from '@/api/handleApiRequest';
import { uid, useQuasar } from 'quasar';
import { useGeneralPurposeModalStore } from '@/store';
import { createDecisionModal } from '@/utils/generalPurposeModalFactory';

enum IFrameEvents {
  OnAuthReady = 'on-auth-ready',
  OnAuthComplete = 'on-auth-complete',
  Authenticate = 'authenticate'
}

const $q = useQuasar()

// state
const srcUri = ref();
const iframeLoaded = ref(false);
const debugVisible = ref(false);
const debugOptionJointsSelected = ref();
const debugOptionsJoints = ref([
  {
    label: `${IFrameEvents.OnAuthComplete}: success`,
    value: { eventName: IFrameEvents.OnAuthComplete, payload: { success: true } }
  },
  {
    label: `${IFrameEvents.OnAuthComplete}: fail`,
    value: { eventName: IFrameEvents.OnAuthComplete }
  }
]);

const debugOptionsPWE = ref([
  {
    label: `${IFrameEvents.Authenticate}: emit`,
    value: { eventName: IFrameEvents.Authenticate, payload: {} }
  }
]);

// local variables
const iframeElementRef = ref<HTMLIFrameElement | null>(null);
let _correlationId = null;
let _token = null;
let _jointsBaseUrl = null;

const handleIncommingMessage = (message: MessageEvent) => {

  console.log('Receiving event from Joints', message.data)
  if (message.source === window && !message.data.spoofed) //ignore own messages
    return;

  //Joints event response handlers
  //This section reacts to our own events and emits a response event Joints would send
  if (message.data.spoofed) {
    if (message.data.eventName?.toLowerCase() === IFrameEvents.Authenticate) {
      postMessage(debugOptionJointsSelected.value.eventName, { ...debugOptionJointsSelected.value.payload, spoofed: true })
      return;
    }
  }

  //PWE event response handlers
  if (message.data.eventName?.toLowerCase() === IFrameEvents.OnAuthReady) {
    iframeLoaded.value = true
    const spoofed = !!debugVisible.value && !!debugOptionJointsSelected.value
    postMessage(IFrameEvents.Authenticate, {
      token: _token,
      correlationId: _correlationId,
      spoofed
    })
  } else if (message.data.eventName?.toLowerCase() === IFrameEvents.OnAuthComplete) {
    if (message.data.success) {
      handleApiRequest((axios) => axios.post('/Login/MedStratAuthenticated', _correlationId), null, $q)
    }
  }
}

const getSignedUri = async () => {
  const response = await handleApiRequest(() => getMedStratSigningUrl(), null, $q)
  _jointsBaseUrl = response.result.baseUrl
  srcUri.value = `${response.result.loginUrl}`
  debugVisible.value = response?.result.debug
  _token = response?.result.digest
  _correlationId = response?.result.correlationId
}

const postMessage = (eventName, payload) => {
  const eventData = { eventName, ...payload }
  console.log(`Sending event to Joints at ${_jointsBaseUrl}`, eventData)

  if (debugOptionJointsSelected.value && debugVisible.value) {
    window.postMessage({ eventName, ...payload });
  } else if (iframeElementRef.value?.contentWindow) {
    iframeElementRef.value.contentWindow.postMessage(eventData, _jointsBaseUrl);
  }

}

const triggerDebugEventPWE = () => {
  debugOptionJointsSelected.value = null
  postMessage(IFrameEvents.Authenticate, {
    token: _token,
    correlationId: _correlationId
  })
}


const triggerDebugEventJoints = () => {
  console.log(debugOptionJointsSelected.value)
  postMessage(IFrameEvents.OnAuthReady, {
    ...debugOptionJointsSelected.value.value,
    spoofed: true
  })
}

onMounted(async () => {
  await getSignedUri();

  window.addEventListener('message', handleIncommingMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleIncommingMessage);
});

</script>

<template>
  <FullViewLayout withHeader>
    <div class="px-20 full-height">
      <q-expansion-item expand-separator icon="fa-solid fa-toolbox" label="Event Testing Console" color="primary"
        v-if="debugVisible" class="mb-20">
        <div class="row items-center py-16 px-16 ">
          <div class="col pt-16 pl-16">
            <zbm-btn @click="triggerDebugEventPWE" :color="$zb.enums.ButtonStdColorEnum.accent"
              :height="$zb.enums.ButtonHeightEnum.lg" label="Trigger PWE Authenticate Event" />
          </div>
          <div class="col-auto pt-16 pr-16">
            <zbm-select-with-validation v-model="debugOptionJointsSelected" option-label="label"
              :options="debugOptionsJoints" option-value="value" label="Joints Event" />

          </div>
          <div class="col-auto pt-16 pl-16">
            <zbm-btn @click="triggerDebugEventJoints" :color="$zb.enums.ButtonStdColorEnum.accent"
              :height="$zb.enums.ButtonHeightEnum.lg" label="Trigger Joints Event" />
          </div>
        </div>
      </q-expansion-item>

      <q-skeleton type="rect" widht="100%" height="100%" v-show="!iframeLoaded" />
      <iframe ref="iframeElementRef" id="MedStratIframe" name="MedStratIframe" v-show="iframeLoaded"
        sandbox="allow-forms allow-modals allow-scripts allow-downloads allow-same-origin" :src="srcUri"
        :style="{ width: '100%', height: debugVisible ? '71vh' : '100%', border: 'none' }">
      </iframe>
    </div>
  </FullViewLayout>
</template>
